import { useMutation } from "@tanstack/react-query";
import {
  AuthConfig,
  createRecordFromArray,
  ListTestResponse,
  queries,
  service,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  Checkbox,
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  notifyError,
  notifySuccess,
  ScrollArea,
  ScrollBar,
  TrashcanIcon,
} from "@theprelude/ds";
import { useState } from "react";
import { useOverlay } from "@/hooks/use-overlay";
import { createQuery } from "@/lib/create-query";
import { DefaultDialog } from "../default-dialog";

const deleteThreatQuery = (authConfig: AuthConfig, id: string) =>
  createQuery({
    queries: [
      queries.detect.tests(authConfig),
      queries.detect.threats(authConfig),
    ],
    queryFn: (tests, threats) => {
      const testsById = createRecordFromArray(tests, "id");
      const threatTests = new Set(
        threats.filter((t) => t.id !== id).flatMap((t) => t.tests),
      );

      const threat = threats.find((t) => t.id === id);
      const relevantTests = threat?.tests
        .map((id) => testsById[id])
        .filter((t): t is ListTestResponse => t !== undefined);

      const testsToDelete =
        relevantTests?.filter((t) => !threatTests.has(t.id)) ?? [];

      return { testsToDelete };
    },
  });

export function DeleteThreatModal(props: {
  id: string;
  open: boolean;
  onOpenChange: (value: boolean) => any;
  close: () => void;
}) {
  return (
    <DefaultDialog open={props.open} onOpenChange={props.onOpenChange}>
      <DeleteThreatModalContent {...props} />
    </DefaultDialog>
  );
}

function DeleteThreatModalContent(props: { id: string; close: () => void }) {
  const [purge, setPurge] = useState<boolean>(false);
  const authConfig = useAuthConfig();
  const overlay = useOverlay();
  const query = deleteThreatQuery(authConfig, props.id).useSuspenseQuery();
  const { testsToDelete } = query.data;

  const deleteThreat = useMutation({
    mutationFn: async () => {
      await service(authConfig).build.deleteThreat({
        threat_id: props.id,
        purge,
      });

      await Promise.allSettled(
        testsToDelete.map((t) =>
          service(authConfig).build.deleteTest({
            test_id: t.id,
            purge,
          }),
        ),
      );
    },
    onSuccess: async () => {
      notifySuccess({
        message: `Successfully deleted threat and tests`,
        dismissible: true,
      });
      close();
      void overlay.close();
    },
    onError: async (error) => {
      notifyError({
        message: "Error occurred while trying to delete threat and tests",
        error,
        dismissible: true,
      });
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          Are you sure you want to delete the following tests and threat?
        </DialogTitle>
      </DialogHeader>
      <DialogBody>
        <span>
          <strong>Important:</strong> You can not undo this action. This will
          delete the following tests:
        </span>
        <ScrollArea className="h-[11rem] py-4">
          {testsToDelete.map((t) => (
            <li key={t.id}>{`${t.name} (*${t.id.slice(32, 36)})`}</li>
          ))}
          <ScrollBar />
        </ScrollArea>
        <div className="flex items-center gap-2">
          <Checkbox checked={purge} onCheckedChange={() => setPurge(!purge)} />
          Delete all associated test results (This will delete all of the data
          permanently)
        </div>
      </DialogBody>
      <DialogFooter>
        <DialogClose asChild>
          <Button intent="secondary">Cancel</Button>
        </DialogClose>
        <Button
          intent="negative"
          loading={deleteThreat.isPending}
          disabled={deleteThreat.isPending}
          icon={<TrashcanIcon />}
          onClick={() => deleteThreat.mutate()}
        >
          Delete tests and threat
        </Button>
      </DialogFooter>
    </>
  );
}
